// src/App.js – versión limpia con PushInit
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Login from './components/Login';
import AdminLogin from './components/AdminLogin';
import usePushNotifications from './hooks/usePushNotifications';
import Home from './pages/Home';
import ProductCategory from './pages/ProductCategory';
import ProductDetail from './pages/ProductDetail';
import CreateProduct from './pages/CreateProduct';
import Navbar from './components/Navbar';
import CreateCategory from './components/CreateCategory';
import LoadingScreen from './components/LoadingScreen';
import Cart from './pages/Cart';
import { CartProvider } from './contexts/CartContext';
import InstallPrompt from './components/InstallPrompt';
import AdPopup from './components/AdPopup';
import CreateAd from './pages/CreateAd';
import axios from 'axios';
import './App.css';

const apiUrl = process.env.REACT_APP_API_URL || 'https://www.nualily.es';
const isSnap = (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') || process.env.REACT_APP_SNAP === 'true';




// Componente auxiliar que inicializa push una vez que el usuario está logeado
function PushInit() {
  usePushNotifications();
  return null;
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  /* -------------------------------------------------------------------- */
  // Splash de carga
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  /* -------------------------------------------------------------------- */
  // Comprobar token / rol guardados en localStorage
  /* -------------------------------------------------------------------- */
 useEffect(() => {
  if (isSnap) {
    // Finge token para que se renderice Home
    setIsAuthenticated(true);
    return;
  }
  const token = localStorage.getItem('token');
  setIsAuthenticated(Boolean(token));
  setIsAdmin(localStorage.getItem('isAdmin') === 'true');
}, []);


  /* -------------------------------------------------------------------- */
  // Evento "Add to Home Screen" (PWA)
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  /* -------------------------------------------------------------------- */
  // Detectar nueva versión de Service Worker
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
              setUpdateAvailable(true);
            }
          });
        });
      });
    }
  }, []);

  /* -------------------------------------------------------------------- */
  // Funciones auxiliares
  /* -------------------------------------------------------------------- */
  const handleInstallClick = () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    deferredPrompt.userChoice.finally(() => {
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    });
  };

  const handleClosePrompt = () => {
    setShowInstallPrompt(false);
    // volver a mostrar en 5 min si el usuario cierra
    setTimeout(() => setShowInstallPrompt(true), 300_000);
  };

 const handleUpdate = () => {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    // 🔹 Oculta el banner
    setUpdateAvailable(false);
    // 🔹 Espera un poco y recarga
    setTimeout(() => {
      window.location.reload();
    }, 300);
  }
};


  if (isLoading) return <LoadingScreen />;

  return (
    <Router>
      <CartProvider>
        {isAuthenticated && (
          <>
            <Navbar
              isAuthenticated={isAuthenticated}
              isAdmin={isAdmin}
              setIsAuthenticated={setIsAuthenticated}
            />
            <PushInit />
          </>
        )}

        <Routes>
          <Route path="/" element={isAuthenticated ? <Home /> : <Login setIsAuthenticated={setIsAuthenticated} setIsAdmin={setIsAdmin} />} />
          <Route path="/admin-login" element={isAuthenticated && isAdmin ? <Home /> : <AdminLogin setIsAuthenticated={setIsAuthenticated} setIsAdmin={setIsAdmin} />} />
          <Route path="/category/:slug" element={isAuthenticated ? <ProductCategory /> : <Login setIsAuthenticated={setIsAuthenticated} setIsAdmin={setIsAdmin} />} />
          <Route path="/product/:id" element={isAuthenticated ? <ProductDetail /> : <Login setIsAuthenticated={setIsAuthenticated} setIsAdmin={setIsAdmin} />} />
          <Route path="/cart" element={isAuthenticated ? <Cart /> : <Login setIsAuthenticated={setIsAuthenticated} setIsAdmin={setIsAdmin} />} />
          <Route path="/create-product" element={isAuthenticated && isAdmin ? <CreateProduct /> : <Login setIsAuthenticated={setIsAuthenticated} setIsAdmin={setIsAdmin} />} />
          <Route path="/create-category" element={isAuthenticated && isAdmin ? <CreateCategory /> : <Login setIsAuthenticated={setIsAuthenticated} setIsAdmin={setIsAdmin} />} />
          <Route path="/create-ad" element={isAuthenticated && isAdmin ? <CreateAd /> : <Login setIsAuthenticated={setIsAuthenticated} setIsAdmin={setIsAdmin} />} />
        </Routes>

        {showInstallPrompt && (
          <InstallPrompt onInstallClick={handleInstallClick} onClose={handleClosePrompt} />
        )}
        {updateAvailable && (
          <div className="update-notification">
            <button onClick={handleUpdate}>Actualizar APP</button>
          </div>
        )}

        <AdPopup isAuthenticated={isAuthenticated} />
      </CartProvider>
    </Router>
  );
}
